import { useEffect, useState, type CSSProperties } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/layout";
import { Button, Card, StatusIndicator } from "../components/ui";
import {
  ApiUnavailableState,
  EmptyState,
  LoadingState,
  NotConfiguredState,
} from "../components/states";
import { useSession } from "../hooks/useSession";
import {
  api,
  ApiError,
  type BotInstance,
  type DiscordGuild,
  type ValidationFinding,
  type Workspace,
} from "../lib/api";

type WizardStep = "workspace" | "connect" | "guild" | "bot" | "validate" | "done";

/** Server-driven onboarding. Nothing is faked: connection state comes from the API. */
export function Onboarding(): JSX.Element {
  const { state } = useSession();

  if (state.kind === "loading") return <LoadingState label="Checking your session…" />;
  if (state.kind === "not-configured")
    return (
      <NotConfiguredState title="Control API URL not configured">
        Set <code>VITE_CONTROL_API_URL</code> to run onboarding.
      </NotConfiguredState>
    );
  if (state.kind === "unavailable") return <ApiUnavailableState />;
  if (state.kind === "anonymous")
    return (
      <>
        <PageHeader title="Welcome to ODIN" />
        <EmptyState title="Please sign in to begin">
          <Link to="/login">
            <Button variant="primary">Sign in with Discord</Button>
          </Link>
        </EmptyState>
      </>
    );

  return <Wizard />;
}

function Wizard(): JSX.Element {
  const [step, setStep] = useState<WizardStep>("workspace");
  const [workspace, setWorkspace] = useState<Workspace | null>(null);
  const [guild, setGuild] = useState<DiscordGuild | null>(null);
  const [bot, setBot] = useState<BotInstance | null>(null);
  const [validation, setValidation] = useState<{ status: string; findings: ValidationFinding[] } | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The default path is workspace → connect (add the shared ODIN bot) → done.
  // Bring-your-own-bot (guild/bot/validate) is a Dedicated-only detour entered
  // from the connect step, so it isn't shown in the top progress rail.
  const steps: WizardStep[] = ["workspace", "connect", "done"];
  const progress = steps.indexOf(step) >= 0 ? steps.indexOf(step) : 1;

  return (
    <>
      <PageHeader title="Set up your Discord server">
        <p>
          BALDER keeps this simple. Create a workspace, then add the ODIN bot to your Discord server in
          one step — no developer setup and no bot of your own to create.
        </p>
      </PageHeader>

      <div className="row" style={{ gap: "0.5rem", marginBottom: "1rem" }} aria-label="Progress">
        {steps.map((s, i) => (
          <span key={s} className={`badge`} style={{ opacity: i <= progress ? 1 : 0.4 }}>
            {i + 1}. {label(s)}
          </span>
        ))}
      </div>

      {error ? (
        <div className="state" role="alert" style={{ borderColor: "var(--color-danger)" }}>
          <p className="state__title">Something needs your attention</p>
          <p>{error}</p>
        </div>
      ) : null}

      {step === "workspace" && (
        <WorkspaceStep
          onError={setError}
          onDone={(w) => {
            setWorkspace(w);
            setError(null);
            setStep("connect");
          }}
        />
      )}

      {step === "connect" && workspace && (
        <ConnectStep
          workspace={workspace}
          onError={setError}
          onUseOwnBot={() => {
            setError(null);
            setStep("guild");
          }}
        />
      )}

      {step === "guild" && workspace && (
        <GuildStep
          workspace={workspace}
          onError={setError}
          onDone={(g) => {
            setGuild(g);
            setError(null);
            setStep("bot");
          }}
        />
      )}

      {step === "bot" && workspace && guild && (
        <BotStep
          workspace={workspace}
          guild={guild}
          onError={setError}
          onDone={(b) => {
            setBot(b);
            setError(null);
            setStep("validate");
          }}
        />
      )}

      {step === "validate" && workspace && bot && (
        <ValidateStep
          workspace={workspace}
          bot={bot}
          onError={setError}
          onDone={(v) => {
            setValidation(v);
            setError(null);
            if (v.findings.every((f) => f.severity !== "BLOCKER")) setStep("done");
          }}
        />
      )}

      {step === "done" && (
        <Card title="Your server is connected" description="Onboarding complete for this phase.">
          <StatusIndicator tone="ok" label={`Bot status: ${bot?.status ?? "connected"}`} />
          <p style={{ marginTop: "0.75rem" }}>
            Live changes stay locked in this phase — ODIN validated your bot without touching your
            server. {validation ? `Validation: ${validation.status}.` : ""}
          </p>
        </Card>
      )}
    </>
  );
}

function label(s: WizardStep): string {
  return { workspace: "Workspace", connect: "Connect", guild: "Server", bot: "Bot", validate: "Validate", done: "Done" }[s];
}

function toMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return "Something went wrong. Please try again.";
}

/**
 * Plans allowed to register their own bot identity. Everyone else gets ODIN's
 * managed all-in-one bot connected for them. This mirrors the `bot.runtime.dedicated`
 * entitlement in packages/billing/src/plan-catalogue.ts and the `requireByoBotPlan`
 * gate in apps/control-api/src/routes.ts — the server is the real enforcement, this
 * only decides whether the detour is offered.
 */
const BYO_PLANS = new Set(["DEDICATED"]);

function ConnectStep({
  workspace,
  onUseOwnBot,
  onError,
}: {
  workspace: Workspace;
  onUseOwnBot: () => void;
  onError: (m: string) => void;
}): JSX.Element {
  const [planCode, setPlanCode] = useState<string | null>(null);

  useEffect(() => {
    api.billing
      .subscription(workspace.id)
      .then((r) => setPlanCode(r.subscription.planCode))
      .catch(() => setPlanCode("FREE"));
  }, [workspace.id]);

  const addOdin = (): void => {
    onError("");
    window.location.href = api.sharedConnectUrl(workspace.id);
  };
  const canByo = planCode !== null && BYO_PLANS.has(planCode);

  return (
    <Card
      title="Add ODIN to your server"
      description="One step: authorize the ODIN bot on the Discord server you manage. Nothing changes on your server without your approval."
    >
      <div className="row" style={{ marginTop: "0.5rem" }}>
        <Button variant="primary" onClick={addOdin}>
          Add ODIN to your server
        </Button>
      </div>
      <p style={{ fontSize: "var(--text-sm)", color: "var(--color-text-muted)", marginTop: "1rem" }}>
        {canByo ? (
          <>
            Prefer to run your own bot identity?{" "}
            <button
              type="button"
              onClick={onUseOwnBot}
              style={{ background: "none", border: "none", padding: 0, color: "var(--brand-primary)", cursor: "pointer", font: "inherit" }}
            >
              Use your own bot instead
            </button>{" "}
            — available on your plan.
          </>
        ) : (
          "Want to run ODIN under your own bot identity instead? Bringing your own bot is available only on the Dedicated plan."
        )}
      </p>
    </Card>
  );
}

function WorkspaceStep({ onDone, onError }: { onDone: (w: Workspace) => void; onError: (m: string) => void }): JSX.Element {
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null);
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.listWorkspaces().then((r) => setWorkspaces(r.workspaces)).catch((e) => onError(toMessage(e)));
  }, [onError]);

  const create = async () => {
    setBusy(true);
    try {
      onDone(await api.createWorkspace(name));
    } catch (e) {
      onError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!workspaces) return <LoadingState label="Loading your workspaces…" />;

  return (
    <Card title="Choose a workspace" description="A workspace holds your servers and settings.">
      {workspaces.length > 0 && (
        <div className="card-grid" style={{ marginBottom: "1rem" }}>
          {workspaces.map((w) => (
            <button key={w.id} className="btn btn--secondary" onClick={() => onDone(w)}>
              {w.name} ({w.role ?? "member"})
            </button>
          ))}
        </div>
      )}
      <div className="row">
        <input
          aria-label="New workspace name"
          placeholder="New workspace name"
          value={name}
          onChange={(e) => setName(e.target.value)}
          style={{ padding: "0.5rem", borderRadius: 6, border: "1px solid var(--color-border-strong)", background: "var(--color-surface-raised)", color: "var(--color-text)" }}
        />
        <Button variant="primary" disabled={busy || name.trim().length < 2} onClick={create}>
          Create workspace
        </Button>
      </div>
    </Card>
  );
}

function GuildStep({ workspace, onDone, onError }: { workspace: Workspace; onDone: (g: DiscordGuild) => void; onError: (m: string) => void }): JSX.Element {
  const [guilds, setGuilds] = useState<DiscordGuild[] | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.listGuilds().then((r) => setGuilds(r.guilds)).catch((e) => onError(toMessage(e)));
  }, [onError]);

  const connect = async (g: DiscordGuild) => {
    setBusy(true);
    try {
      await api.connectGuild(workspace.id, g.discordGuildId);
      onDone(g);
    } catch (e) {
      onError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  if (!guilds) return <LoadingState label="Reading your Discord servers…" />;
  const manageable = guilds.filter((g) => g.canManage);
  if (manageable.length === 0)
    return <EmptyState title="No servers you can manage">You need Manage Server or Administrator on a Discord server.</EmptyState>;

  return (
    <Card title="Select a server to connect" description="Only servers you can manage are shown.">
      <div className="card-grid">
        {manageable.map((g) => (
          <button
            key={g.discordGuildId}
            className="btn btn--secondary"
            disabled={busy || (g.alreadyConnected && g.connectedTenantId !== workspace.id)}
            onClick={() => connect(g)}
          >
            {g.name}
            {g.alreadyConnected ? " · connected" : ""}
          </button>
        ))}
      </div>
    </Card>
  );
}

function BotStep({ workspace, guild, onDone, onError }: { workspace: Workspace; guild: DiscordGuild; onDone: (b: BotInstance) => void; onError: (m: string) => void }): JSX.Element {
  const [applicationId, setApplicationId] = useState("");
  const [name, setName] = useState("ODIN Bot");
  const [busy, setBusy] = useState(false);

  const register = async () => {
    setBusy(true);
    try {
      onDone(
        await api.registerBotInstance(workspace.id, {
          discordApplicationId: applicationId,
          configuredName: name,
          guildId: guild.discordGuildId,
        }),
      );
    } catch (e) {
      onError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Create your own bot"
      description="On the Dedicated plan you can run ODIN under your own bot identity. Create a Discord application, then paste its Application ID."
    >
      <ol style={{ color: "var(--color-text-muted)", lineHeight: 1.7 }}>
        <li>Open the Discord Developer Portal → New Application.</li>
        <li>Add a Bot user, then copy the Application ID from General Information.</li>
      </ol>
      <div className="row" style={{ marginTop: "0.5rem" }}>
        <input aria-label="Bot name" value={name} onChange={(e) => setName(e.target.value)} style={inputStyle} />
        <input aria-label="Discord application ID" placeholder="Application ID" value={applicationId} onChange={(e) => setApplicationId(e.target.value)} style={inputStyle} />
        <Button variant="primary" disabled={busy || applicationId.trim().length < 5} onClick={register}>
          Continue
        </Button>
      </div>
    </Card>
  );
}

function ValidateStep({ workspace, bot, onDone, onError }: { workspace: Workspace; bot: BotInstance; onDone: (v: { status: string; findings: ValidationFinding[] }) => void; onError: (m: string) => void }): JSX.Element {
  const [inviteUrl, setInviteUrl] = useState<string | null>(null);
  const [token, setToken] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ status: string; findings: ValidationFinding[] } | null>(null);

  useEffect(() => {
    api.inviteUrl(workspace.id, bot.id).then((r) => setInviteUrl(r.inviteUrl)).catch((e) => onError(toMessage(e)));
  }, [workspace.id, bot.id, onError]);

  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.submitToken(workspace.id, bot.id, token);
      setToken(""); // never keep the token in state after submission
      setResult(r.validation);
      onDone(r.validation);
    } catch (e) {
      onError(toMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card
      title="Add the bot and validate"
      description="Use the invite link to add the bot, then paste its token. ODIN validates read-only — it never changes your server."
    >
      {inviteUrl ? (
        <p>
          <a href={inviteUrl} target="_blank" rel="noreferrer">
            Open the bot invite link ↗
          </a>
        </p>
      ) : (
        <LoadingState label="Preparing invite link…" />
      )}
      <div className="row" style={{ marginTop: "0.5rem" }}>
        <input aria-label="Bot token" type="password" placeholder="Bot token" value={token} onChange={(e) => setToken(e.target.value)} style={inputStyle} />
        <Button variant="primary" disabled={busy || token.trim().length < 30} onClick={submit}>
          Validate
        </Button>
      </div>
      <p style={{ fontSize: "var(--text-xs)", color: "var(--color-text-faint)", marginTop: "0.5rem" }}>
        Your token is sent once over a secure connection and stored encrypted. It’s never shown again
        or kept in your browser.
      </p>

      {result ? (
        <div style={{ marginTop: "1rem", display: "grid", gap: "0.5rem" }}>
          <StatusIndicator tone={result.status === "passed" || result.status === "warning" ? "ok" : "danger"} label={`Validation: ${result.status}`} />
          {result.findings.map((f, i) => (
            <div key={i} className="card" style={{ padding: "0.75rem" }}>
              <strong>{f.title}</strong>
              <div style={{ fontSize: "var(--text-sm)", color: "var(--color-text-muted)" }}>{f.explanation}</div>
              {f.severity === "BLOCKER" || f.severity === "ERROR" ? (
                <div style={{ fontSize: "var(--text-sm)", color: "var(--color-warn)" }}>{f.remediation}</div>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
    </Card>
  );
}

const inputStyle: CSSProperties = {
  padding: "0.5rem",
  borderRadius: 6,
  border: "1px solid var(--color-border-strong)",
  background: "var(--color-surface-raised)",
  color: "var(--color-text)",
  minWidth: 220,
};
